

import { DashboardLayout } from "@/components/dashboard/dashboard-layout"
import { Switch } from "@/components/ui/switch"
import Image from "@/src/components/Image"
import { useState } from "react"

interface SettingItem {
  id: string
  title: string
  description: string
  warning?: string
  enabled: boolean
  titleColor?: "green" | "gray"
}

export default function SlipChecksSettingsPage() {
  const [selectedSettingId, setSelectedSettingId] = useState<string | null>(null)
  const [amountCondition, setAmountCondition] = useState<"greater" | "less" | "equal">("equal")
  const [settings, setSettings] = useState<SettingItem[]>([
    {
      id: "check-when-quota-expires",
      title: "ตรวจสอบสลิปเมื่อโควต้าหมด",
      description: "หักเครดิตเมื่อสลิปโควต้าแพ็กเกจหมด",
      enabled: false,
      titleColor: "gray",
    },
    {
      id: "check-duplicate",
      title: "ตรวจสอบสลิปซ้ำ",
      description: "แจ้งเตือนเมื่อมีสลิปซ้ำ",
      enabled: false,
      titleColor: "gray",
    },
    {
      id: "check-expense",
      title: "ตรวจสอบสลิปรายจ่าย",
      description: "แจ้งเตือนเมื่อบัญชีผู้โอนตรงกับบัญชีของคุณในระบบ",
      warning: "*จะไม่ตรวจเงื่อนไขยอดเงินโอนเเละวันที่โอน",
      enabled: true,
      titleColor: "green",
    },
    {
      id: "check-days-limit",
      title: "ตรวจสอบสลิปไม่เกิน XX วัน",
      description: "แจ้งเตือนเมื่อมีวันที่โอนเกิน",
      enabled: true,
      titleColor: "green",
    },
    {
      id: "check-amount",
      title: "ตรวจสอบสลิปจำนวนเงินโอน",
      description: "แจ้งเตือนเมื่อมียอดโอน",
      enabled: false,
      titleColor: "gray",
    },
    {
      id: "compact-notifications",
      title: "การแสดงผลแจ้งเตือนแบบย่อ",
      description: "เปลี่ยนการแสดงผลแจ้งเตือนใน LINE เป็นแบบย่อ",
      enabled: false,
      titleColor: "gray",
    },
    {
      id: "line-webhook",
      title: "LINE Webhook",
      description: "Forward ข้อความ LINE OA เข้าระบบของคุณ",
      enabled: false,
      titleColor: "gray",
    },
  ])

  const toggleSetting = (id: string) => {
    setSettings((prev) =>
      prev.map((setting) => (setting.id === id ? { ...setting, enabled: !setting.enabled } : setting)),
    )
  }

  const selectedSetting = settings.find((s) => s.id === selectedSettingId)

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-4 lg:mb-6">
          <h1 className="text-xl lg:text-2xl font-bold text-gray-900">ตั้งค่าการตรวจสอบสลิป</h1>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-lg shadow-sm">
          <h2 className="text-lg lg:text-xl font-semibold text-gray-900 p-6">ตั้งค่าการตรวจสอบสลิป</h2>
          <hr className="border-gray-200" />

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr]">
            {/* Left Section - Settings list */}
            <div className="p-6">
              <div className="space-y-4">
                {settings.map((setting) => (
                  <button
                    key={setting.id}
                    onClick={() => setSelectedSettingId(setting.id)}
                    className={`w-full justify-start font-semibold bg-gray-50 border rounded-lg px-6 py-3 h-auto text-left transition-colors ${
                      selectedSettingId === setting.id ? "border-brand-primary" : "border-gray-300"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 space-y-1">
                        <h3 className="font-semibold text-brand-primary">{setting.title}</h3>
                        <p className="text-sm text-gray-500">{setting.description}</p>
                        {setting.warning && <p className="text-sm text-brand-red">{setting.warning}</p>}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-sm text-gray-600">{setting.enabled ? "เปิด" : "ปิด"}</span>
                        <Switch
                          checked={setting.enabled}
                          onCheckedChange={() => toggleSetting(setting.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Divider */}
            <div className="hidden lg:block border-l border-gray-200"></div>

            {/* Right Section - Detail view */}
            <div className="hidden lg:block">
              {!selectedSetting ? (
                <div className="p-6 flex flex-col items-center justify-center h-full">
                  <Image
                    src="/icons/icon-settings.svg"
                    alt="Settings icon"
                    width={192}
                    height={192}
                    className="w-48 h-48 mb-4 opacity-20"
                  />
                  <p className="text-xl text-gray-300 font-medium text-center">เลือกการตั้งค่าตรวจสอบสลิป</p>
                  <p className="text-lg text-gray-300 text-center mt-2">เพื่อดูคำอธิบายเพิ่มเติม</p>
                </div>
              ) : (
                <div className="p-6">
                  {selectedSetting.id === "check-when-quota-expires" && (
                    <div className="space-y-6">
                      {/* Title */}
                      <h5 className="text-xl font-semibold text-gray-900 text-center">แนะนำวิธีการใช้งาน</h5>

                      {/* เมื่อเปิดใช้งาน Section */}
                      <div className="space-y-3">
                        <h6 className="text-lg font-semibold text-brand-blue">เมื่อเปิดใช้งาน</h6>
                        <div className="">
                          <p className="text-sm text-gray-700 leading-relaxed">
                            และโควต้าตรวจสอบสลิปหมด ยังสามารถ ตรวจสอบสลิปได้หากบัญชีผู้ใช้มีเครดิตคงเหลือ โดยจะคิดค่า บริการตามอัตราในแพ็กเกจปัจจุบันที่ใช้งานอยู่
                          </p>
                        </div>
                      </div>

                      {/* เมื่อปิดใช้งาน Section */}
                      <div className="space-y-3">
                        <h6 className="text-lg font-semibold text-brand-red">เมื่อปิดใช้งาน</h6>
                        <div className="">
                          <p className="text-sm text-gray-700 leading-relaxed">
                            และโควต้าตรวจสอบสลิปหมด ระบบจะไม่ตรวจสอบรูปภาพ หรือสลิปได้อีก จนกว่าจะมีรอบการชำระเงิน จนกว่าจะมีรอบการชำระเงินหรือซื้อเครดิต แพคเกจที่ใช้งาน
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedSetting.id === "check-duplicate" && (
                    <div className="space-y-6">
                      {/* Title */}
                      <h5 className="text-xl font-semibold text-gray-900 text-center">แนะนำวิธีการใช้งาน</h5>

                      {/* เมื่อเปิดใช้งาน Section */}
                      <div className="space-y-3">
                        <h6 className="text-lg font-semibold text-brand-blue">เมื่อเปิดใช้งาน</h6>
                        <div className="">
                          <p className="text-sm text-gray-700 leading-relaxed">
                            หากมีการส่งสลิปซ้ำระบบจะทำการบันทึกข้อมูลเป็นสลิปซ้ำ และไม่มีการคิดค่าบริการในการตรวจสอบสลิปนั้น
                          </p>
                        </div>
                      </div>

                      {/* เมื่อปิดใช้งาน Section */}
                      <div className="space-y-3">
                        <h6 className="text-lg font-semibold text-brand-red">เมื่อปิดใช้งาน</h6>
                        <div className="">
                          <p className="text-sm text-gray-700 leading-relaxed">
                            หากมีการส่งสลิปซ้ำระบบจะทำการตรวจสอบและบันทึกข้อมูลตามปกติ โดยจะมีการคิดค่าบริการตามอัตราในแพ็กเกจปัจจุบัน
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedSetting.id === "check-expense" && (
                    <div className="space-y-6">
                      {/* Title */}
                      <h5 className="text-xl font-semibold text-gray-900 text-center">แนะนำวิธีการใช้งาน</h5>

                      {/* เมื่อเปิดใช้งาน Section */}
                      <div className="space-y-3">
                        <h6 className="text-lg font-semibold text-brand-blue">เมื่อเปิดใช้งาน</h6>
                        <div className="">
                          <p className="text-sm text-gray-700 leading-relaxed">
                            สลิปที่บัญชีผู้โอนตรงกับบัญชีในระบบจะถูกเก็บข้อมูลเป็นรายจ่าย
                          </p>
                        </div>
                      </div>

                      {/* เมื่อปิดใช้งาน Section */}
                      <div className="space-y-3">
                        <h6 className="text-lg font-semibold text-brand-red">เมื่อปิดใช้งาน</h6>
                        <div className="">
                          <p className="text-sm text-gray-700 leading-relaxed">
                            จะไม่มีการตรวจสอบบัญชีผู้โอน ทุกสลิปจะถูกบันทึกเป็นรายรับ
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedSetting.id === "check-days-limit" && (
                    <div className="space-y-6">
                      {/* Title */}
                      <h5 className="text-xl font-semibold text-gray-900 text-center">แนะนำวิธีการใช้งาน</h5>

                      {/* เมื่อเปิดใช้งาน Section */}
                      <div className="space-y-3">
                        <h6 className="text-lg font-semibold text-brand-blue">เมื่อเปิดใช้งาน</h6>
                        <div className="">
                          <p className="text-sm text-gray-700 leading-relaxed">
                            หากวันที่ทำรายการสลิปเคินระยะเวลาที่กำหนดจะระบบจะทำการบันทึกข้อมูลตามปกติ โดยจะมีการคิดค่าบริการตามอัตราในแพ็กเกจปัจจุบัน
                          </p>
                        </div>
                      </div>

                      {/* เมื่อปิดใช้งาน Section */}
                      <div className="space-y-3">
                        <h6 className="text-lg font-semibold text-brand-red">เมื่อปิดใช้งาน</h6>
                        <div className="">
                          <p className="text-sm text-gray-700 leading-relaxed">
                            ระบบจะทำการตรวจสอบและบันทึกข้อมูลตามปกติ โดยจะมีการคิดค่าบริการตามอัตราในแพ็กเกจปัจจุบัน
                          </p>
                        </div>
                      </div>

                      {/* Input field for days */}
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <h6 className="grow text-base font-semibold text-gray-900">แจ้งเตือนเมื่อวันที่โอนเกิน</h6>
                          <input
                            type="number"
                            defaultValue={7}
                            min={1}
                            className="w-32 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                          />
                          <span className="text-sm text-gray-700">วัน</span>
                          <button className="ml-auto px-6 py-2 bg-brand-primary text-white font-medium rounded-lg hover:bg-brand-primary-dark transition-colors">
                            บันทึก
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedSetting.id === "check-amount" && (
                    <div className="space-y-6">
                      {/* Title */}
                      <h5 className="text-xl font-semibold text-gray-900 text-center">แนะนำวิธีการใช้งาน</h5>

                      {/* เมื่อเปิดใช้งาน Section */}
                      <div className="space-y-3">
                        <h6 className="text-lg font-semibold text-brand-blue">เมื่อเปิดใช้งาน</h6>
                        <div className="">
                          <p className="text-sm text-gray-700 leading-relaxed">
                            หากยอดเงินในสลิปไม่ตรงเงื่อนไขที่ตั้งค่าไว้ ระบบจะทำการบันทึกข้อมูลเป็นสลิปยอดเงินไม่ตรง
                          </p>
                        </div>
                      </div>

                      {/* เมื่อปิดใช้งาน Section */}
                      <div className="space-y-3">
                        <h6 className="text-lg font-semibold text-brand-red">เมื่อปิดใช้งาน</h6>
                        <div className="">
                          <p className="text-sm text-gray-700 leading-relaxed">
                            ระบบจะทำการตรวจสอบและบันทึกข้อมูลตามปกติ โดยจะมีการคิดค่าบริการตามอัตราในแพ็กเกจปัจจุบัน
                          </p>
                        </div>
                      </div>

                      {/* Condition selection buttons */}
                      <div className="space-y-3">
                        <h6 className="text-base font-semibold text-gray-900">เงื่อนไขการในตรวจสอบ</h6>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => setAmountCondition("greater")}
                            className={`px-6 py-2 border-2 font-medium rounded-lg transition-colors ${
                              amountCondition === "greater"
                                ? "border-brand-blue bg-brand-blue text-white"
                                : "border-gray-300 text-gray-700 hover:border-brand-blue hover:text-brand-blue"
                            }`}
                          >
                            มากกว่า
                          </button>
                          <button
                            onClick={() => setAmountCondition("less")}
                            className={`px-6 py-2 border-2 font-medium rounded-lg transition-colors ${
                              amountCondition === "less"
                                ? "border-brand-blue bg-brand-blue text-white"
                                : "border-gray-300 text-gray-700 hover:border-brand-blue hover:text-brand-blue"
                            }`}
                          >
                            ไม่เกิน
                          </button>
                          <button
                            onClick={() => setAmountCondition("equal")}
                            className={`px-6 py-2 border-2 font-medium rounded-lg transition-colors ${
                              amountCondition === "equal"
                                ? "border-brand-blue bg-brand-blue text-white"
                                : "border-gray-300 text-gray-700 hover:border-brand-blue hover:text-brand-blue"
                            }`}
                          >
                            เท่ากับ
                          </button>
                        </div>
                      </div>

                      {/* Amount input field */}
                      <div className="space-y-3">
                        <h6 className="text-base font-semibold text-gray-900">จำนวนเงิน</h6>
                        <div className="flex items-center gap-3">
                          <input
                            type="number"
                            defaultValue={1}
                            min={0}
                            step={0.01}
                            placeholder="1 บาท"
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                          />
                          <button className="px-6 py-2 bg-brand-primary text-white font-medium rounded-lg hover:bg-brand-primary-dark transition-colors">
                            บันทึก
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedSetting.id === "compact-notifications" && (
                    <div className="space-y-6">
                      {/* Title */}
                      <h5 className="text-xl font-semibold text-gray-900 text-center">แนะนำวิธีการใช้งาน</h5>

                      {/* เมื่อเปิดใช้งาน Section */}
                      <div className="space-y-3">
                        <h6 className="text-lg font-semibold text-brand-blue">เมื่อเปิดใช้งาน</h6>
                        <div className="">
                          <p className="text-sm text-gray-700 leading-relaxed">
                            เมื่อเปิดใช้งาน แสดงผลการแจ้งเตือนในรูปแบบข้อความเลื่อนด้านล่างเพื่อดูตัวอย่าง
                          </p>
                        </div>
                      </div>

                      {/* เมื่อปิดใช้งาน Section */}
                      <div className="space-y-3">
                        <h6 className="text-lg font-semibold text-brand-red">เมื่อปิดใช้งาน</h6>
                        <div className="">
                          <p className="text-sm text-gray-700 leading-relaxed">
                            เมื่อปิดใช้งาน แสดงผลการแจ้งเตือนในรูปแบบกราฟฟิก ดูตัวอย่างที่ด้านล่าง
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedSetting.id === "line-webhook" && (
                    <div className="space-y-6">
                      {/* Title */}
                      <h5 className="text-xl font-semibold text-gray-900 text-center">แนะนำวิธีการใช้งาน</h5>

                      {/* Instructions */}
                      <div className="space-y-3">
                        <div className="text-sm text-gray-700 space-y-2">
                          <p>
                            <span className="font-semibold">1.</span> ร้านค้าของท่านต้องเชื่อมต่อกับ LINE OA อย่างสมบูรณ์
                            <a href="#" className="text-brand-blue hover:underline">
                              *หน้าการเชื่อมต่อ {">"} LINE OA ต้องแสดงสถานะว่า "เชื่อมต่อแล้ว"
                            </a>
                          </p>
                          <p>
                            <span className="font-semibold">2.</span> ท่านจำเป็นต้องกรอก Webhook url ให้ถูกต้อง
                          </p>
                          <p>
                            <span className="font-semibold">3.</span>{" "}
                            <span className="text-brand-red">เมื่อเปิดใช้งาน</span> และ Thaislipcheck ได้รับข้อความจาก LINE OA ของท่าน
                            ระบบจะส่งข้อมูลทั้งหมดไปยัง Webhook url ของท่าน
                          </p>
                        </div>
                      </div>

                      {/* LINE OA Webhook URL */}
                      <div className="space-y-3">
                        <h6 className="text-base font-semibold text-gray-900">LINE OA Webhook</h6>
                        <input
                          type="url"
                          placeholder="กรอก Webhook URL"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                        />
                      </div>

                      {/* Key */}
                      <div className="space-y-3">
                        <h6 className="text-base font-semibold text-gray-900">Key</h6>
                        <input
                          type="text"
                          placeholder="กรอก Key"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                        />
                      </div>

                      {/* Value */}
                      <div className="space-y-3">
                        <h6 className="text-base font-semibold text-gray-900">Value</h6>
                        <input
                          type="text"
                          placeholder="กรอก Value"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                        />
                      </div>

                      {/* Save button */}
                      <div className="flex justify-end">
                        <button className="px-6 py-2 bg-brand-primary text-white font-medium rounded-lg hover:bg-brand-primary-dark transition-colors">
                          บันทึก
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}
